import { Language } from '@yleisradio/areena-types';
import parseIsoDuration from 'parse-iso-duration';
import { MediaLanguageCode } from '../appTypings';

const programContext = (programId: string): AnalyticsContext => {
  const programContext = {
    schema: 'iglu:fi.yle.content/program/jsonschema/2-0-1',
    data: {
      program_id: programId,
      predicate: 'media_playing',
    },
  };

  return programContext;
};

const getServiceContext = (service_id: string) => {
  const serviceContext = {
    schema: 'iglu:fi.yle.content/service/jsonschema/2-0-1',
    data: {
      service_id,
      predicate: 'media_playing',
    },
  };

  return serviceContext;
};

const getLiveMediaContext = (
  isLiveMedia: boolean,
  dvrWindow: string
): AnalyticsContext => {
  const liveContext = {
    schema: 'iglu:fi.yle.player/live_stream/jsonschema/1-0-0',
    data: { dvr_window: parseIsoDuration(dvrWindow) / 1000 },
  };

  return liveContext;
};

const getMediaContext = (media_id: string) => {
  const mediaContext = {
    schema: 'iglu:fi.yle.content/media/jsonschema/2-1-1',
    data: {
      media_id,
      predicate: 'media_playing',
    },
  };
  return mediaContext;
};

export type ContextSection =
  | 'tv-frontpage'
  | 'player-page'
  | 'podcast-frontpage'
  | 'program-page'
  | 'series-page'
  | 'areena-view-page'
  | 'areena-package-page';

export type ContextServiceLanguage = 'fin' | 'swe';
export type ContextContentLanguage = MediaLanguageCode | 'und' | 'mul';

const mapLanguageToContextServiceLanguage = (
  language: Language
): ContextServiceLanguage => {
  switch (language) {
    case 'fi':
      return 'fin';
    case 'sv':
      return 'swe';
  }
};

type AnalyticsContextParams = {
  section: ContextSection;
  service_language: ContextServiceLanguage;
  content_language: ContextContentLanguage;
  context_index: number;
};

const getYleContext = ({
  section,
  service_language,
  content_language,
  context_index,
}: AnalyticsContextParams) => {
  const yleContext = {
    schema: 'iglu:fi.yle/yle_context/jsonschema/2-3-0',
    data: {
      main_service: 'yle-areena',
      service: 'areena-leanback',
      section,
      service_language,
      content_language,
      context_index,
      wrapper_type: 'web',
    },
  };

  return yleContext;
};

const publicationContext = (publication_id: string) => {
  const publicationContext = {
    schema: 'iglu:fi.yle.content/publication/jsonschema/2-0-0',
    data: {
      publication_id,
      predicate: 'media_playing',
    },
  };
  return publicationContext;
};

type GetMediaTrackingContextProps = {
  section: ContextSection;
  service_language: Language;
  content_language: ContextContentLanguage;
  isLiveMedia: boolean;
  dvr_window: string | undefined;
  programId: string;
  service_id: string | undefined;
  media_id: string;
  publication_id: string | undefined;
};

export type AnalyticsContext = {
  schema: string;
  data: Record<string, unknown>;
};

export const getContext = ({
  isLiveMedia,
  dvr_window,
  programId,
  section,
  service_language,
  content_language,
  service_id,
  media_id,
  publication_id,
}: GetMediaTrackingContextProps): AnalyticsContext[] => {
  const context: AnalyticsContext[] = [];

  context.push(
    getYleContext({
      section,
      service_language: mapLanguageToContextServiceLanguage(service_language),
      content_language,
      context_index: 1,
    })
  );

  if (isLiveMedia && dvr_window) {
    context.push(getLiveMediaContext(isLiveMedia, dvr_window));
  }

  if (service_id) {
    context.push(getServiceContext(service_id));
  }

  context.push(programContext(programId));

  if (media_id) {
    context.push(getMediaContext(media_id));
  }
  if (publication_id) {
    context.push(publicationContext(publication_id));
  }

  return context;
};
